import { Button } from '@platform/ui-library';
import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { Markdown } from '@app/form_components';
import { useServices } from '@app/hooks';
import { GenerateRequest, TimeInMs } from '@app/types';
import styles from './ExplainCodePopover.module.scss';

export type ExplainCodePopoverProps = (
  Pick<GenerateRequest, 'currentCode' | 'functionInputs'>
  & {
    /** Whether the popover is open. */
    isOpen: boolean;
    /** Callback to close the popover. */
    onClose: () => void;
  }
);

enum ExplainState {
  error = 'error',
  idle = 'idle',
  loading = 'loading',
  success = 'success'
}

const popoverWidth = 500;

const loadingPhrases = [
  'Running git blame on every line...',
  'Checking if this works on my machine...',
  'Consulting Stack Overflow circa 2012...',
  'Pretending to understand the regex...',
  'Debating tabs vs spaces while analyzing...',
  'Unminifying someone\'s life choices...',
  'Counting nested callbacks one by one...',
  'Searching for the missing semicolon...',
  'Reading code that documents itself (allegedly)...',
  'Finding bugs Maged swears aren\'t there...',
  'Figuring out why this works but not how...',
  'Stepping through 47 layers of abstraction...',
  'Tracing logic through a maze of if-else...',
  'Decoding variable names chosen by chaos...',
  'Cross-referencing with ancient commit messages...',
  'Reviewing code nobody dared refactor...',
  'Interpreting comments that say "fix later"...',
  'Analyzing the spaghetti one strand at a time...',
  'Reverse-engineering what the author meant...',
  'Making sense of this so you don\'t have to...'
];

const explainPrompt = [
  'Explain what this code does.',
  'Provide a clear, concise explanation covering:',
  'the overall purpose, key operations, how input variables are used,',
  'and any side effects (API calls, session variable changes, etc).',
  'Do NOT include code. Do NOT suggest improvements.'
].join(' ');

/**
 * Lightweight popover that displays an AI-generated explanation of the
 * current code. Calls the existing Gemini endpoint and renders the
 * result as readable text.
 */
export const ExplainCodePopover: FC<ExplainCodePopoverProps> = ({
  currentCode,
  functionInputs,
  isOpen,
  onClose
}) => {
  const { geminiService } = useServices();
  const [explanation, setExplanation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState(ExplainState.idle);
  const [wasTruncated, setWasTruncated] = useState(false);
  const [loadingPhrase, setLoadingPhrase] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});

  // Rotate the loading phrase every few seconds while loading.
  useEffect(() => {
    if (state !== ExplainState.loading) {
      return;
    }

    const randomizeLoadingPhrase = () => {
      const randomIndex = Math.floor(Math.random() * loadingPhrases.length);
      setLoadingPhrase(loadingPhrases[randomIndex]);
    };

    randomizeLoadingPhrase();
    const interval = setInterval(() => {
      randomizeLoadingPhrase();
    }, 2 * TimeInMs.second);
    return () => clearInterval(interval);
  }, [state]);

  const fetchExplanation = useCallback(async () => {
    if (!currentCode.trim()) {
      setExplanation(null);
      setState(ExplainState.success);
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setState(ExplainState.loading);
    setError(null);

    try {
      const data = await geminiService.generate({
        conversationHistory: [],
        currentCode,
        functionInputs,
        userPrompt: explainPrompt
      }, controller.signal);

      setExplanation(data.rawResponse);
      setWasTruncated(data.wasTruncated ?? false);
      setState(ExplainState.success);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      setError((err instanceof Error) ? err.message : 'An unexpected error occurred');
      setState(ExplainState.error);
    }
  }, [geminiService, currentCode, functionInputs]);

  // Fetch explanation when popover opens & compute position
  useEffect(() => {
    if (isOpen) {
      fetchExplanation();

      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        // Centre the popover within the containing popup / modal
        const popup = anchorRef.current.closest('.w2ui-popup') as HTMLElement | null;
        const container = popup ?? document.documentElement;
        const containerRect = container.getBoundingClientRect();
        const centeredLeft = containerRect.left + (containerRect.width - popoverWidth) / 2;
        const left = Math.max(8, Math.min(centeredLeft, window.innerWidth - popoverWidth - 8));
        setPopoverStyle({
          left,
          top: rect.bottom + 8
        });
      }
    } else {
      // Reset state when closed
      setExplanation(null);
      setError(null);
      setState(ExplainState.idle);
      setWasTruncated(false);
      abortControllerRef.current?.abort();
    }
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [isOpen, fetchExplanation]);

  return (
    <>
      <div className={styles.anchor} ref={anchorRef} />
      {isOpen && createPortal(
        <>
          <div className={styles.overlay} onClick={onClose} />
          <div className={styles.popover} style={popoverStyle}>
            <div className={styles.header}>
              <div>
                <h3>Code Explanation</h3>
                <p className={styles.disclaimer}>AI-generated results may be inaccurate. Please validate before use.</p>
              </div>
              <button
                className={styles.closeButton}
                onClick={onClose}
                title="Close"
                type="button"
              >
                <i className="fe icon-x" />
              </button>
            </div>

            {state === ExplainState.loading && (
              <div className={styles.loading}>
                <span className="glyphicon glyphicon-refresh glyphicon-refresh-animate" />
                {loadingPhrase}
              </div>
            )}

            {state === ExplainState.error && (
              <div className={styles.error}>
                <span>{error}</span>
                <Button
                  className={styles.retryButton}
                  onClick={fetchExplanation}
                  type="button"
                >
                  <i className="fe icon-refresh-cw" />
                  Retry
                </Button>
              </div>
            )}

            {state === ExplainState.success && ((explanation) ? (
              <Markdown className={styles.body} content={explanation} />
            ) : (
              <div className={styles.emptyState}>No code to explain.</div>
            ))}

            {state === ExplainState.success && wasTruncated && (
              <div className={styles.truncationWarning}>
                The explanation was truncated due to length limits.
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
};
